import React, { useEffect, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { fileToCompressedDataURL } from "@/lib/image";
import { toast } from "sonner";

export default function ImageUploadField({ label, value, onChange, hint, dataTestid, maxDim = 1400 }) {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(value || "");

  useEffect(() => { setUrlInput(value || ""); }, [value]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)."); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataURL(file, { maxDim, quality: 0.85 });
      onChange(dataUrl);
      toast.success("Image ready. Don't forget to Save.");
    } catch {
      toast.error("Could not read the image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1.5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="w-28 h-20 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
          {value ? <img src={value} alt="Preview" className="w-full h-full object-cover" /> : <span className="text-slate-400 text-xs">No image</span>}
        </div>
        <div className="flex-1 w-full space-y-2">
          <input
            type="text"
            placeholder="https://… or paste image URL"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onBlur={() => onChange(urlInput)}
            data-testid={dataTestid && `${dataTestid}-url`}
            className="w-full bg-white rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
          />
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-4 py-2 text-xs font-medium cursor-pointer transition-colors">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {value ? "Replace" : "Upload"}
              <input type="file" accept="image/*" onChange={onFile} data-testid={dataTestid} className="hidden" />
            </label>
            {value && (
              <button type="button" onClick={() => { onChange(""); setUrlInput(""); }} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-3 py-1.5">
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
          {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
      </div>
    </div>
  );
}
